import { registerHandler, Sha256Email } from "../handler";
import { trackTask } from "src/utils/monitoring/events/tasks";
import { generateEventId } from "../utils";
import { send } from "src/utils/fetch";
import { LibCategories, waitForConsent } from "src/utils/consent";
import { appConfig } from "src/config";

import {
  MED_PINTEREST_TAG_ID,
  WEIGHT_PINTERST_TAG_ID,
} from "src/utils/constants";
import { shouldFireMedPixel } from "src/utils/userSegment/features";
import getStore from "src/utils/redux/store";
import { getCountryCode } from "src/utils/meristemContext";

declare const pintrk: any;

const isTestEnv = appConfig.NOOM_ENV !== "production";

interface PinterestParams {
  planId: string;
  duration: number;
}

const NOOM_WEIGHT_CONVERSION_EVENT = "checkout";
const NOOM_MED_PURCHASE_INTENT_EVENT = "checkout";

function convertToPinConvParam(params: Record<string, any>) {
  const convParam: string[] = [];
  Object.keys(params).forEach((key) => {
    const param = params[key];
    if (param != null && param) {
      convParam.push(`${key}:${param.toString()}`);
    }
  });
  return convParam.join(",");
}

function trackPinterestConversion(order, plan, event) {
  const pinParams: PinterestParams = {
    planId: plan.braintree_id,
    duration: plan.billing_cycle_duration,
  };
  const convParam = convertToPinConvParam(pinParams);
  pintrk("track", event, {
    value: order.eltv_13_months,
    order_quantity: 1,
    currency: "USD",
    // adding the subscription id as event_id too for deduplication purposes
    event_id: order.subscriptionId,
    order_id: order.subscriptionId,
    conv_param1: convParam,
  });
}

function trackPinterestPurchaseIntent(eventId, value) {
  pintrk("track", NOOM_MED_PURCHASE_INTENT_EVENT, {
    value,
    order_quantity: 1,
    currency: "USD",
    event_id: eventId,
    // we don't have order_id as subscription is not created yet, so we're using event_id as order_id
    order_id: eventId,
  });
}

function trackPinterestLead(email: Sha256Email) {
  // Enhanced Match: Update med pixel with email (fires on all pages)
  if (email) {
    pintrk("load", MED_PINTEREST_TAG_ID, { em: email });
  }
  pintrk("track", "lead");

  // Weight pixel: only fire on weight pages (unchanged behavior)
  if (!shouldFireMedPixel()) {
    if (email) {
      pintrk("load", WEIGHT_PINTERST_TAG_ID, { em: email });
    }
    pintrk("track", "lead");
  }
}

// https://developers.pinterest.com/docs/ad-tools/conversion-tag/
export default function registerHandlers() {
  registerHandler("lead", ({ emailHashes }) => {
    trackPinterestLead(emailHashes.sha256);
  });

  registerHandler("purchase", ({ order, plan, emailHashes }) => {
    // Med pixel: fire "signup" for weight purchases (on all pages)
    pintrk("load", MED_PINTEREST_TAG_ID, { em: emailHashes.sha256 });
    trackPinterestConversion(order, plan, "signup");

    // Weight pixel: fire "checkout" only on weight pages (unchanged behavior)
    if (!shouldFireMedPixel()) {
      pintrk("load", WEIGHT_PINTERST_TAG_ID, { em: emailHashes.sha256 });
      trackPinterestConversion(order, plan, NOOM_WEIGHT_CONVERSION_EVENT);
    }
  });

  registerHandler("purchaseIntent", ({ estimatedValue, emailHashes, plan }) => {
    waitForConsent([LibCategories.targetingCookies]).then(async () => {
      const task = trackTask("trackPinterestPurchaseIntent");
      try {
        task.start();
        const { userData } = getStore().getState();
        const eventId = generateEventId();
        // Client-side Pinterest pixel tracking (existing functionality)
        pintrk("load", MED_PINTEREST_TAG_ID, { em: emailHashes.sha256 });
        trackPinterestPurchaseIntent(eventId, estimatedValue);

        // Server-side CAPI tracking
        const eventData = {
          event_name: "checkout",
          event_id: eventId,
          event_source_url: window.location.href,
          test_mode: isTestEnv,
          user_data: {
            hashed_email: emailHashes.sha256,
            country: getCountryCode(),
            external_id: userData?.user_id,
          },
          custom_data: {
            value: estimatedValue,
            order_id: eventId,
            currency: "USD",
            content_ids: [plan.braintree_id],
            num_items: 1,
          },
        };

        await send("POST", `/pixel/v1/i/pinterestCapiPixel/`, eventData);
        task.success();
      } catch (err) {
        task.failed(err);
      }
    });
  });
}
